import type { Editor } from '@tiptap/react';
import { EditorContent } from '@tiptap/react';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, MessageSquare } from 'lucide-react';
import { NoteAttachments } from '../../ui/NoteAttachments';
import type { NoteAttachment } from '../../../lib/types';

export interface NoteTabProps {
  /**
   * Istanza TipTap CONDIVISA con la tab Attività (stessa `description`/`note` nella
   * fonte), creata e gestita dalla SHELL (Task 12, non ancora scritta) — questo
   * componente NON crea, distrugge o configura l'editor, si limita a renderizzarlo.
   * `null` è ammesso (`useEditor` può restituirlo per un istante al mount) e gestito
   * via optional chaining su tutti i comandi/stati della toolbar, come nella fonte.
   */
  editor: Editor | null;
  /**
   * File selezionati per l'allegato alla nota, non ancora caricati: la nota non
   * esiste ancora come riga DB finché non viene salvata dalla shell (stesso
   * "pre-creation mode" di `NoteAttachments` nella fonte — mai upload immediato).
   */
  files: File[];
  onFilesChange(files: File[]): void;
  /**
   * true nel flusso "nota obbligatoria per completare il cambio di stato" (drag&drop
   * Kanban). Nella fonte (LeadStatusUpdateDrawer.tsx) governa solo elementi della
   * SHELL — nasconde lo switch tab e cambia il titolo header in "Aggiungi una nota
   * per completare il cambio di stato" (:1813, :1840-1843) — mai la label del campo
   * nota, che resta sempre "Nota" indipendentemente da questo flag. Questo
   * componente riceve la prop per completezza del contratto (porting fedele: non
   * introduce comportamento non presente nella fonte) — è la SHELL (Task 12) a
   * usarla per header/tab-switcher/validazione submit.
   */
  requireNote: boolean;
  /**
   * Aggiunte dalla shell (Task 12), tutte additive e con default retrocompatibile:
   * - `label`/`showAttachments`: nella fonte l'editor è UNICO e renderizzato in ENTRAMBE le
   *   tab, con label "Nota"/"Descrizione" a seconda della tab (:2413) e allegati riservati
   *   alla sola nota (:2468). La shell monta quindi questo componente una sola volta, fuori
   *   dal tab switch, e ne cambia label/allegati — così l'istanza TipTap non viene mai
   *   smontata al cambio tab (regressione storica "nota sparisce").
   * - `disabled`/`toast`: props reali di `NoteAttachments` (fonte :2477-2478), senza le quali
   *   gli errori di upload/validazione file restano silenziosi.
   */
  label?: string;
  showAttachments?: boolean;
  disabled?: boolean;
  toast?: { success(message: string): void; error(message: string): void };
}

/** `onUpload` di `NoteAttachments` non è mai invocata in pre-creation mode (il drop
 * handler della fonte fa return prima di chiamarla), ma la prop è obbligatoria a
 * livello di tipo: no-op locale che rispetta la firma di `uploadNoteAttachment`
 * (leadNoteStore.ts) senza importare query/servizi in questo componente controllato. */
async function noopUpload(): Promise<NoteAttachment | null> {
  return null;
}

/** Idem per `onDelete`: la fonte passa `async () => false` perché in pre-creation
 * mode non esistono ancora allegati caricati da poter eliminare (`attachments`
 * sempre `[]` qui sotto). */
async function noopDelete(): Promise<boolean> {
  return false;
}

/**
 * Tab "Nota" del drawer condiviso Attività/Nota. Componente CONTROLLATO: nessuno
 * stato interno, nessuna query — editor TipTap e file selezionati arrivano dalla
 * shell via props.
 *
 * Fonte: LeadStatusUpdateDrawer.tsx :2410-2481 — blocco editor+toolbar condiviso
 * (label "Nota"/"Descrizione" a seconda di `actionType`, qui sempre "Nota" perché
 * questo componente è dedicato alla sola tab nota) e blocco allegati riservato ad
 * `actionType === 'note'`. Classi Tailwind portate 1:1.
 */
export function NoteTab({
  editor,
  files,
  onFilesChange,
  requireNote: _requireNote,
  label = 'Nota',
  showAttachments = true,
  disabled = false,
  toast,
}: NoteTabProps) {
  return (
    <>
      <div>
        <label className="flex items-center gap-1.5 text-xs font-medium text-[#787774] dark:text-gray-400 mb-1">
          <MessageSquare className="h-3 w-3" strokeWidth={1.5} />
          {label}
        </label>

        {/* WYSIWYG Editor - nudo, senza box (mockup: .editor/.ed-bar/.ed-content) */}
        <div>
          <div className="flex gap-0.5 py-0.5">
            <button
              type="button"
              onClick={() => editor?.chain().focus().toggleBold().run()}
              className={`p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] ${editor?.isActive('bold') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]' : ''}`}
            >
              <Bold className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              className={`p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] ${editor?.isActive('italic') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]' : ''}`}
            >
              <Italic className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => {
                const url = window.prompt('URL');
                if (url) {
                  editor?.chain().focus().setLink({ href: url }).run();
                }
              }}
              className={`p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] ${editor?.isActive('link') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]' : ''}`}
            >
              <LinkIcon className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              className={`p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] ${editor?.isActive('bulletList') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]' : ''}`}
            >
              <List className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              className={`p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] ${editor?.isActive('orderedList') ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]' : ''}`}
            >
              <ListOrdered className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
            </button>
          </div>
          <EditorContent
            editor={editor}
            className="min-h-[80px] px-1.5 py-1.5 focus:outline-none text-[#37352F] dark:text-white text-[13px]"
          />
        </div>
      </div>

      {showAttachments && (
        <NoteAttachments
          attachments={[]}
          onUpload={noopUpload}
          onDelete={noopDelete}
          preCreationMode={true}
          selectedFiles={files}
          onFilesSelected={onFilesChange}
          disabled={disabled}
          toast={toast}
          variant="ghost-row"
        />
      )}
    </>
  );
}
